/**
 * Custom Connector Tools - Create, manage, and update custom connectors
 *
 * Provides tools for:
 * - Listing custom connectors
 * - Creating connectors from OpenAPI specs
 * - Adding operations to existing connectors
 * - Updating and deleting connectors
 */

import { z } from "zod";
import { displayValue } from "../utils/format.js";
import type { ConnectorMetadataApi, CustomConnectorDefinition, OpenApiSpec, ConnectionParameter } from "../api/connector-metadata.js";
import { logger } from "../utils/logger.js";
import { validateHttpsUrl, isPrivateHost } from "../utils/security.js";
import { parse as parseYaml } from "yaml";
import { ConfirmationRequiredError } from "../utils/errors.js";
import { discoverProtectedResourceMetadata } from "../utils/oauth-resource-metadata.js";

// =============================================================================
// List Custom Connectors
// =============================================================================

export const listCustomConnectorsInputSchema = z.object({
  environment: z.string().optional().describe("Environment ID (uses default if omitted)"),
});

// =============================================================================
// OAuth scope diagnosis is intentionally informational: declarations and public
// metadata do not reveal a connection's token grants or an operation's needs.
export const diagnoseConnectorAuthInputSchema = z.object({
  connectorId: z
    .string()
    .trim()
    .min(1)
    .max(256)
    .describe("Connector name in the current environment"),
  discover: z
    .boolean()
    .optional()
    .default(true)
    .describe(
      "Read public metadata only when resourceUrl is explicitly supplied. No discovery without it.",
    ),
  resourceUrl: z
    .string()
    .min(1)
    .max(2048)
    .optional()
    .describe(
      "Explicit verified HTTPS protected-resource URL, not its OAuth authorization URL. Public port 443 only; no credentials, query or fragment.",
    ),
});

export type DiagnoseConnectorAuthInput = z.infer<typeof diagnoseConnectorAuthInputSchema>;

export const diagnoseConnectorAuthTool = {
  name: "diagnose_connector_auth",
  description:
    "Inspect the OAuth scopes declared by a custom connector in the current environment. " +
    "Optionally compare public RFC 9728 advertised scopes using an explicit verified resourceUrl; never infer a resource from its OAuth authority. " +
    "Advertised scopes are not necessarily required or exhaustive, and this tool cannot inspect connection grants. " +
    "Lists exactly associated connections without reauthorizing, changing permissions or recommending every advertised scope.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connectorId: {
        type: "string",
        minLength: 1,
        maxLength: 256,
        description: "Connector name in the current environment",
      },
      discover: {
        type: "boolean",
        default: true,
        description: "Discover only with explicit resourceUrl; false reports declarations only.",
      },
      resourceUrl: {
        type: "string",
        minLength: 1,
        maxLength: 2048,
        description:
          "Verified HTTPS protected-resource URL, not OAuth authority. Public port 443 only; no credentials, query or fragment.",
      },
    },
    required: ["connectorId"],
  },
};

function diagnosticRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function diagnosticLabel(value: unknown): string {
  if (typeof value !== "string") return "(not returned)";
  return Array.from(value.slice(0, 256), (character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127 ? " " : character;
  }).join("");
}

function exactConnectorName(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const match = /^(?:\/providers\/Microsoft\.PowerApps\/apis\/)?([a-z0-9_.-]+)\/?$/i.exec(
    value.trim(),
  );
  return match?.[1]?.toLowerCase();
}

export async function handleDiagnoseConnectorAuth(
  context: { api: ConnectorMetadataApi },
  input: unknown,
): Promise<string> {
  const parsed = diagnoseConnectorAuthInputSchema.parse(input);
  logger.info({ connectorId: parsed.connectorId }, "Executing diagnose_connector_auth");

  const connector = await context.api.getCustomConnector(parsed.connectorId);
  const lines = [
    "## " + diagnosticLabel(connector.properties.displayName) + " — authentication",
    "",
  ];
  const connParams = diagnosticRecord(connector.properties.connectionParameters);
  if (connParams === undefined) {
    lines.push(
      "Connection-parameter metadata was not returned or is malformed; authentication and declared scopes are unknown.",
    );
    return lines.join("\n");
  }
  const oauthParams = Object.entries(connParams).filter(
    ([, param]) => diagnosticRecord(param)?.type === "oauthSetting",
  );
  if (oauthParams.length === 0) {
    lines.push(
      "No OAuth 2.0 settings were found in the returned connection parameters. This does not inspect other authentication sources.",
    );
    return lines.join("\n");
  }
  // Do not silently choose one OAuth configuration when more than one exists.
  if (oauthParams.length !== 1) {
    lines.push(
      "Multiple OAuth configurations were returned; scope attribution is ambiguous. Inspect the connector's authentication configuration.",
    );
    return lines.join("\n");
  }
  const oauth = diagnosticRecord(diagnosticRecord(oauthParams[0]?.[1])?.oAuthSettings);
  const rawDeclared = oauth?.scopes;
  let declared: string[] | undefined;
  if (
    Array.isArray(rawDeclared) &&
    rawDeclared.length <= 256 &&
    rawDeclared.every(
      (scope): scope is string =>
        typeof scope === "string" && /^[\x21\x23-\x5b\x5d-\x7e]{1,256}$/.test(scope),
    )
  ) {
    declared = [...new Set(rawDeclared)];
    lines.push(
      "Declared scopes (" + declared.length + "):",
      declared.length === 0 ? "  (none)" : "  " + declared.join(" "),
    );
  } else {
    lines.push(
      "Declared scopes: unknown (not returned, malformed or beyond the bounded 256-scope limit).",
    );
  }
  lines.push(
    "",
    "Required scopes: unknown. Obtain the specific operation's requirements from authoritative documentation or an actual insufficient-scope response.",
    "Granted connection scopes: unknown. Connector declarations and public metadata do not inspect the connection's access token or consent.",
  );

  if (!parsed.discover) {
    lines.push("", "Public metadata discovery disabled; no resource request was made.");
  } else if (parsed.resourceUrl === undefined) {
    lines.push(
      "",
      "Public metadata discovery not attempted: provide a verified resourceUrl. OAuth authorization URLs and connector proxy paths do not establish resource identity.",
    );
  } else {
    const discovered = await discoverProtectedResourceMetadata(parsed.resourceUrl);
    if (!discovered.ok) {
      lines.push(
        "",
        "Public metadata discovery unavailable (" + discovered.reason + "): " + discovered.message,
      );
      if (discovered.reason === "invalid_metadata") {
        lines.push(
          "Discovery limitation: metadata validation is all-or-nothing. Malformed fields or more than 100 advertised scopes can make resource and issuer metadata unavailable; the specific field was not identified.",
        );
      }
    } else {
      lines.push(
        "",
        "Public metadata verified for resource: " + discovered.resource,
        "Source: " + discovered.metadataUrl,
      );
      // This reader already bounds and validates issuer URLs; report exact
      // identifiers without fetching them or deriving an issuer from endpoints.
      const authorizationServers = discovered.authorizationServers;
      if (authorizationServers === undefined) {
        lines.push("Resource-advertised authorization servers: not returned; no issuer inferred.");
      } else {
        lines.push(
          "Resource-advertised authorization servers (" + authorizationServers.length + "):",
          ...(authorizationServers.length === 0
            ? ["  (empty advertised list; no authorization server identified)"]
            : authorizationServers.map((issuer) => "  " + issuer)),
        );
      }
      lines.push(
        "Authorization-server metadata was not fetched. Connector/issuer alignment, DCR support and runtime authentication were not verified. An OAuth authorization endpoint or its origin is not an issuer identifier.",
      );
      const supported = discovered.scopesSupported;
      if (supported === undefined) {
        lines.push(
          "Advertised scopes: not returned. This does not establish that the resource supports no scopes.",
        );
      } else {
        lines.push(
          "Advertised scopes (" + supported.length + "):",
          supported.length === 0 ? "  (empty advertised list)" : "  " + supported.join(" "),
        );
        if (declared !== undefined) {
          const advertisedOnly = supported.filter((scope) => !declared.includes(scope));
          const declaredOnly = declared.filter((scope) => !supported.includes(scope));
          lines.push(
            "Advertised but not declared (" +
              advertisedOnly.length +
              "): " +
              (advertisedOnly.join(" ") || "(none)"),
            "Declared but not advertised (" +
              declaredOnly.length +
              "): " +
              (declaredOnly.join(" ") || "(none)"),
          );
        }
        lines.push(
          "Advertisements may omit supported scopes. Neither difference proves a missing required scope or an invalid declaration. Request only the minimum scopes justified for the intended operation; do not add every advertised scope.",
        );
      }
    }
  }

  lines.push(
    "",
    "Changing a connector's declarations does not itself grant permissions to an existing connection. Reauthorization may be needed after a verified scope change; no reauthorization was performed.",
  );
  try {
    const live = await context.api.listConnections();
    const target = exactConnectorName(connector.name);
    if (target === undefined || target !== exactConnectorName(parsed.connectorId)) {
      lines.push(
        "Associated connections: unknown because the returned connector identity did not match the requested name.",
      );
    } else {
      const connections = live.filter(
        (connection) => exactConnectorName(connection.properties.apiId) === target,
      );
      lines.push(
        "Associated connections visible in the current environment (" + connections.length + "):",
      );
      for (const connection of connections.slice(0, 100)) {
        lines.push(
          "  " +
            diagnosticLabel(connection.name) +
            " (" +
            diagnosticLabel(connection.properties.displayName) +
            ")",
        );
      }
      if (connections.length > 100) lines.push("  (truncated to 100 associated connections)");
      if (connections.length === 0)
        lines.push("  (none returned; this does not prove no connections exist)");
      lines.push("Their granted scopes and need for reauthorization were not verified.");
    }
  } catch {
    lines.push(
      "Associated connections: unknown because the connection list could not be read. This is not an empty connection list.",
    );
  }
  return lines.join("\n");
}

// =============================================================================
// OAuth scope update (scopes only)
// =============================================================================

/** A scope token as OAuth 2.0 defines it (RFC 6749 §3.3): no spaces or quotes. */
const OAUTH_SCOPE_TOKEN = /^[\x21\x23-\x5B\x5D-\x7E]+$/;

export function normalizeScopeList(scopes: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of scopes) {
    const scope = raw.trim();
    if (scope === "" || seen.has(scope)) continue;
    seen.add(scope);
    out.push(scope);
  }
  return out;
}

/**
 * The next scope list, from either a full replacement or an add/remove pair.
 * Order is preserved and duplicates collapse, so a caller re-adding a scope
 * that is already present is a no-op rather than a duplicate entry.
 */
export function resolveNextScopes(args: {
  current: readonly string[];
  scopes?: readonly string[] | undefined;
  addScopes?: readonly string[] | undefined;
  removeScopes?: readonly string[] | undefined;
}): string[] | string {
  const replacing = args.scopes !== undefined;
  const adjusting = args.addScopes !== undefined || args.removeScopes !== undefined;
  if (replacing && adjusting) {
    return "Error: pass either scopes (a full replacement list) or addScopes/removeScopes, not both.";
  }
  if (!replacing && !adjusting) {
    return "Error: nothing to change — pass scopes, addScopes or removeScopes.";
  }
  const proposed = replacing
    ? normalizeScopeList(args.scopes!)
    : normalizeScopeList([
        ...args.current.filter((scope) => !(args.removeScopes ?? []).includes(scope)),
        ...(args.addScopes ?? []),
      ]);
  const bad = proposed.filter((scope) => !OAUTH_SCOPE_TOKEN.test(scope));
  if (bad.length > 0) {
    return `Error: not valid OAuth scope tokens (a scope cannot contain spaces or quotes): ${bad.join(", ")}`;
  }
  if (proposed.length === 0) {
    return "Error: refusing to leave a connector with no scopes — an empty scope list makes every authorization request fail.";
  }
  return proposed;
}

/**
 * Plan only Swagger 2 OAuth declaration edits. Explicit operation security
 * overrides the root security (OAS 2.0 §6.4.7); never widen those overrides or
 * non-OAuth requirements. Refuse an incomplete inspection before any write.
 */
function planScopeSwaggerUpdate(
  original: unknown,
  next: readonly string[],
): { swagger: OpenApiSpec; explicitOperationSecurityCount: number } | string {
  const doc = diagnosticRecord(original);
  const definitions = diagnosticRecord(doc?.["securityDefinitions"]);
  const paths = diagnosticRecord(doc?.["paths"]);
  const invalid = "Refusing to write: the original uploaded Swagger security cannot be completely inspected within the supported Swagger 2.0 safety limits. No connector update was sent.";
  if (
    doc?.["swagger"] !== "2.0" || Object.hasOwn(doc, "x-ms-paths") || !definitions || !paths ||
    Object.keys(definitions).length > 64 || Object.keys(paths).length > 5000 || next.length > 512
  ) return invalid;

  const oauthNames = new Set<string>();
  for (const [name, raw] of Object.entries(definitions)) {
    const definition = diagnosticRecord(raw);
    if (!definition || !["oauth2", "apiKey", "basic"].includes(String(definition["type"]))) return invalid;
    if (definition["type"] === "oauth2") oauthNames.add(name);
  }
  if (oauthNames.size === 0) return invalid;
  if (oauthNames.size > 1) {
    return "Refusing to write: multiple OAuth Swagger security schemes have no verified mapping to the connection parameter. No connector update was sent.";
  }
  const nextSet = new Set(next);
  let inspected = 0;
  const inspectSecurity = (value: unknown, operation: boolean): string | undefined => {
    if (!Array.isArray(value) || value.length > 256) return invalid;
    for (const raw of value) {
      const entry = diagnosticRecord(raw);
      if (!entry || Object.keys(entry).length > 64) return invalid;
      for (const [name, scopes] of Object.entries(entry)) {
        if (
          !Object.hasOwn(definitions, name) || !Array.isArray(scopes) || scopes.length > 512 ||
          (inspected += 1 + scopes.length) > 100_000 ||
          !scopes.every((scope): scope is string => typeof scope === "string" && OAUTH_SCOPE_TOKEN.test(scope))
        ) return invalid;
        if (operation && oauthNames.has(name) && scopes.some((scope) => !nextSet.has(scope))) {
          return "Refusing to write: the proposed scopes omit an existing explicit OAuth operation requirement. Operation-level security is preserved, not widened or removed. Review that requirement separately; no connector update was sent.";
        }
      }
    }
    return undefined;
  };

  if (Object.hasOwn(doc, "security")) {
    const error = inspectSecurity(doc["security"], false);
    if (error) return error;
  }
  let explicitOperationSecurityCount = 0;
  for (const [pathName, rawPath] of Object.entries(paths)) {
    if (pathName.startsWith("x-")) continue;
    const path = diagnosticRecord(rawPath);
    // A referenced path could hide an operation requirement; never resolve it
    // through another transport or assume that it is safe to drop a scope.
    if (!pathName.startsWith("/") || !path || Object.hasOwn(path, "$ref")) return invalid;
    for (const method of ["get", "put", "post", "delete", "options", "head", "patch"]) {
      if (!Object.hasOwn(path, method)) continue;
      const operation = diagnosticRecord(path[method]);
      if (!operation || Object.hasOwn(operation, "$ref")) return invalid;
      if (!Object.hasOwn(operation, "security")) continue;
      explicitOperationSecurityCount += 1;
      const error = inspectSecurity(operation["security"], true);
      if (error) return error;
    }
  }

  const securityDefinitions = Object.fromEntries(Object.entries(definitions).map(([name, definition]) => [
    name,
    oauthNames.has(name)
      ? { ...diagnosticRecord(definition), scopes: Object.fromEntries(next.map((scope) => [scope, scope])) }
      : definition,
  ]));
  const security = doc["security"] as Array<Record<string, string[]>> | undefined;
  return {
    swagger: {
      ...doc,
      securityDefinitions,
      ...(security === undefined ? {} : {
        security: security.map((entry) => Object.fromEntries(Object.entries(entry).map(([name, scopes]) => [
          name, oauthNames.has(name) ? [...next] : scopes,
        ]))),
      }),
    } as unknown as OpenApiSpec,
    explicitOperationSecurityCount,
  };
}

function scopeChangeTimestamp(value: unknown): number | undefined {
  if (typeof value !== "string" || value.length > 64) return undefined;
  const parts = /^(\d{4})-(\d{2})-(\d{2})T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,9})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.exec(value);
  if (!parts) return undefined;
  const year = Number(parts[1]);
  const month = Number(parts[2]);
  const day = Number(parts[3]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month < 1 || month > 12 || day < 1 || day > days[month - 1]!) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : undefined;
}

export const updateConnectorOauthScopesInputSchema = z
  .object({
    connectorId: z.string().describe("Connector name, e.g. 'shared_my-2dconnector-5f...'"),
    scopes: z.array(z.string()).optional().describe("Full replacement scope list"),
    addScopes: z.array(z.string()).optional().describe("Scopes to add, keeping the rest"),
    removeScopes: z.array(z.string()).optional().describe("Scopes to remove, keeping the rest"),
    expectedCurrentScopes: z
      .array(z.string())
      .optional()
      .describe("Abort if the connector's current scopes differ from this (stale-state protection)"),
    confirm: z
      .boolean()
      .optional()
      .default(false)
      .describe("false (default) previews the change without writing; true applies it"),
    confirmDelayMs: z
      .number()
      .int()
      .min(0)
      .max(30000)
      .optional()
      .default(3000)
      .describe("Delay between the two confirmation reads (default 3000ms). 0 makes the second read immediate."),
    environment: z.string().optional().describe("Environment ID (uses default if omitted)"),
  })
  .strict();

export type UpdateConnectorOauthScopesInput = z.infer<typeof updateConnectorOauthScopesInputSchema>;

export const updateConnectorOauthScopesTool = {
  name: "update_connector_oauth_scopes",
  description:
    "Change the OAuth 2.0 scopes declared by a custom connector and its top-level Swagger OAuth security, preserving credentials, non-OAuth security and explicit operation-level requirements. " +
    "update_custom_connector cannot do this: any OAuth field there rotates the whole client and demands the client secret, which the platform never returns. " +
    "Previews by default (confirm=false). No-change compares only connectionParameters declarations, not OpenAPI consistency or grants. " +
    "Confirmed writes require the original uploaded Swagger 2.0 definition and refuse scopes that omit explicit OAuth operation requirements; rewritten proxy Swagger is never used as fallback. " +
    "Two-read declaration confirmation requires both scope reads to match and modification timestamps to advance; it does not verify stored OpenAPI or runtime. " +
    "Changing scopes does NOT grant them — every existing connection keeps the set it consented to until it is re-authorized.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connectorId: { type: "string", description: "Connector name" },
      scopes: { type: "array", items: { type: "string" }, description: "Full replacement scope list" },
      addScopes: { type: "array", items: { type: "string" }, description: "Scopes to add" },
      removeScopes: { type: "array", items: { type: "string" }, description: "Scopes to remove" },
      expectedCurrentScopes: {
        type: "array",
        items: { type: "string" },
        description: "Abort if current scopes differ from this (stale-state protection)",
      },
      confirm: { type: "boolean", description: "false previews (default); true applies" },
      confirmDelayMs: {
        type: "integer",
        description: "Delay between the two confirmation reads (default 3000ms)",
      },
      environment: { type: "string", description: "Environment ID" },
    },
    required: ["connectorId"],
    additionalProperties: false,
  },
};

export async function handleUpdateConnectorOauthScopes(
  api: ConnectorMetadataApi,
  input: unknown
): Promise<string> {
  const parsed = updateConnectorOauthScopesInputSchema.parse(input);
  const confirmDelayMs = parsed.confirmDelayMs;
  if (parsed.environment !== undefined) api = api.forEnvironment(parsed.environment);
  logger.info({ connectorId: parsed.connectorId, confirm: parsed.confirm }, "Executing update_connector_oauth_scopes");

  const existing = await api.getCustomConnector(parsed.connectorId);
  const changedTimeBefore = (existing.properties as Record<string, unknown>)["changedTime"];
  const connParams = (existing.properties.connectionParameters ?? {}) as Record<string, ConnectionParameter>;
  const tokenKeys = Object.keys(connParams).filter(
    (key) => (connParams[key] as Record<string, unknown> | undefined)?.["type"] === "oauthSetting",
  );
  if (tokenKeys.length > 1) {
    return "Refusing to change or compare scopes: multiple OAuth connection parameters have no verified scope mapping. No connector update was sent.";
  }
  const tokenKey = tokenKeys[0];
  if (tokenKey === undefined) {
    return `"${existing.properties.displayName}" does not use OAuth 2.0, so it has no scopes to change.`;
  }
  const tokenParam = connParams[tokenKey] as unknown as Record<string, unknown>;
  const oauth = diagnosticRecord(tokenParam["oAuthSettings"]);
  if (oauth === undefined) {
    return `"${existing.properties.displayName}" has an OAuth parameter with no oAuthSettings — repair it in the portal before changing scopes.`;
  }

  const currentRaw = oauth["scopes"];
  if (!Array.isArray(currentRaw) || currentRaw.length > 512 || !currentRaw.every((scope): scope is string => typeof scope === "string" && OAUTH_SCOPE_TOKEN.test(scope))) {
    return "Refusing to change or compare scopes: the current OAuth scope declarations are missing, malformed or exceed the safety limit. No connector update was sent.";
  }
  const current = normalizeScopeList(currentRaw);

  // Stale-state protection: a concurrent edit means the caller is reasoning
  // about a scope list that no longer exists, and a blind write would drop
  // whatever the other change added.
  if (parsed.expectedCurrentScopes !== undefined) {
    const expected = normalizeScopeList(parsed.expectedCurrentScopes);
    if (expected.join(" ") !== current.join(" ")) {
      return (
        `Refusing to write: the connector's scopes are not what you expected.\n` +
        `  expected: ${expected.join(" ") || "(none)"}\n` +
        `  actual:   ${current.join(" ") || "(none)"}\n` +
        `Someone changed them since you read. Re-read with get_custom_connector or diagnose_connector_auth and decide again.`
      );
    }
  }

  const next = resolveNextScopes({
    current,
    scopes: parsed.scopes,
    addScopes: parsed.addScopes,
    removeScopes: parsed.removeScopes,
  });
  if (typeof next === "string") return next;

  const added = next.filter((scope) => !current.includes(scope));
  const removed = current.filter((scope) => !next.includes(scope));

  const lines = [`## ${existing.properties.displayName} — OAuth scopes`, ""];
  lines.push(`current (${current.length}): ${current.join(" ") || "(none)"}`);
  lines.push(`proposed (${next.length}): ${next.join(" ")}`);
  lines.push("");
  lines.push(added.length > 0 ? `adding (${added.length}): ${added.join(" ")}` : "adding: nothing");
  lines.push(
    removed.length > 0
      ? `⚠ removing (${removed.length}): ${removed.join(" ")} — a connection relying on a removed scope loses that access at its next authorization`
      : "removing: nothing",
  );

  if (added.length === 0 && removed.length === 0) {
    lines.push(
      "",
      "No change — only connectionParameters OAuth scope declarations were compared, and they already match.",
      "OpenAPI/root/operation security consistency and effective connection grants were not verified. No connector update or reauthorization was sent.",
    );
    return lines.join("\n");
  }

  if (!parsed.confirm) {
    lines.push(
      "",
      "PREVIEW ONLY — nothing was written. Re-run with confirm=true to apply.",
      "Pass expectedCurrentScopes with the current list above to make the write abort if anything changes in between.",
    );
    return lines.join("\n");
  }

  // Edit the document as it was UPLOADED. The platform's stored copy is a
  // rewrite (proxy host, /{connectionId} prefixes) and writing it back would
  // point the connector at its own proxy — the same trap update_custom_connector
  // documents.
  let original: OpenApiSpec | null = null;
  try {
    if (typeof api.getCustomConnectorOriginalDefinition === "function") {
      original = await api.getCustomConnectorOriginalDefinition(existing);
    }
  } catch {
    // Do not expose a failed SAS fetch URL, token or upstream error body.
  }
  if (!original) {
    return "Refusing to write: the original uploaded Swagger definition is unavailable. The stored proxy-rewritten Swagger is not a safe fallback. No connector update was sent.";
  }
  const swaggerPlan = planScopeSwaggerUpdate(original, next);
  if (typeof swaggerPlan === "string") return swaggerPlan;
  const { swagger, explicitOperationSecurityCount } = swaggerPlan;
  lines.push(
    "",
    `Explicit operation-level security is preserved unchanged (${explicitOperationSecurityCount} declarations); non-OAuth security is not widened.`,
  );

  // Update declarations and top-level OAuth security only. Explicit operation
  // requirements remain intentionally separate and are never broadened;
  // clientSecret is never sent, because the platform does not return it and
  // sending a blank would destroy the connector's credentials.
  const nextOauth: Record<string, unknown> = { ...oauth, scopes: next };
  const nextConnParams: Record<string, ConnectionParameter> = {
    ...connParams,
    [tokenKey]: { ...tokenParam, oAuthSettings: nextOauth } as unknown as ConnectionParameter,
  };
  const backendService = existing.properties.backendService?.serviceUrl
    ? { serviceUrl: existing.properties.backendService.serviceUrl }
    : undefined;
  const definition: CustomConnectorDefinition = {
    properties: {
      description: existing.properties.description,
      iconBrandColor: existing.properties.iconBrandColor,
      connectionParameters: nextConnParams,
      swagger,
      ...(backendService !== undefined ? { backendService } : {}),
      ...(existing.properties.policyTemplateInstances !== undefined
        ? { policyTemplateInstances: existing.properties.policyTemplateInstances }
        : {}),
      ...(existing.properties.capabilities !== undefined ? { capabilities: existing.properties.capabilities } : {}),
    },
  };

  try {
    await api.updateCustomConnector(parsed.connectorId, definition);
  } catch (error) {
    return `Error updating connector scopes: ${describeApiError(error)}`;
  }

  // Read back rather than trusting the write: the platform acknowledges an
  // update with an empty body, so "no error" is not evidence the scopes landed.
  //
  // One read is not enough. Verified live on a real connector: the PATCH was
  // accepted, an immediate read returned the NEW scopes, and every read a few
  // seconds later returned the OLD ones with `changedTime` still at its
  // previous value — the platform had uploaded the OpenAPI document but never
  // applied the resource change. A single readback reported "Applied" for a
  // write that did not stick. So confirm twice, separated in time, and treat
  // an unmoved `changedTime` as evidence the resource was not written.
  const readScopes = async (): Promise<{ scopes: string[] | undefined; changedTime: unknown }> => {
    const after = await api.getCustomConnector(parsed.connectorId);
    const afterParams = (after.properties.connectionParameters ?? {}) as Record<string, ConnectionParameter>;
    const afterOauth = (afterParams[tokenKey] as unknown as Record<string, unknown> | undefined)?.[
      "oAuthSettings"
    ] as Record<string, unknown> | undefined;
    const raw = afterOauth?.["scopes"];
    return {
      scopes: Array.isArray(raw) && raw.every((v): v is string => typeof v === "string" && OAUTH_SCOPE_TOKEN.test(v))
        ? normalizeScopeList(raw)
        : undefined,
      changedTime: (after.properties as Record<string, unknown>)["changedTime"],
    };
  };

  let first: { scopes: string[] | undefined; changedTime: unknown } | undefined;
  let second: { scopes: string[] | undefined; changedTime: unknown } | undefined;
  try {
    first = await readScopes();
    await new Promise((resolve) => setTimeout(resolve, confirmDelayMs));
    second = await readScopes();
  } catch {
    // Fall through to an honest "could not confirm" rather than claiming success.
  }

  const wanted = next.join(" ");
  if (second === undefined || second.scopes === undefined) {
    lines.push("", "⚠ NOT CONFIRMED: the write was accepted but the connector could not be read back, so the stored scopes are unknown.");
  } else if (second.scopes.join(" ") !== wanted) {
    const firstMatched = first?.scopes?.join(" ") === wanted;
    lines.push("", "⚠ NOT CONFIRMED: the latest scope declaration read differs from the accepted request.");
    lines.push(`  sent:   ${wanted}`);
    lines.push(`  stored: ${second.scopes.join(" ") || "(none)"}`);
    if (firstMatched) {
      lines.push(
        "  The first read after the write DID show the new scopes and a later read did not. Reversion or a concurrent change is possible; the cause is not verified.",
      );
    }
    if (changedTimeBefore !== undefined && second.changedTime === changedTimeBefore) {
      lines.push(
        "  The latest changedTime is unchanged from before the request; a persisted connector modification is not confirmed.",
      );
    }
    lines.push("  Inspect the connector before deciding on another change; do not retry the write automatically.");
  } else if (first?.scopes?.join(" ") !== wanted) {
    lines.push("", "⚠ NOT CONFIRMED: the write was accepted and the second declaration read matched, but the first did not. Two-read confirmation failed; inspect the connector before relying on it.");
  } else {
    const beforeTime = scopeChangeTimestamp(changedTimeBefore);
    const firstTime = scopeChangeTimestamp(first.changedTime);
    const secondTime = scopeChangeTimestamp(second.changedTime);
    if (beforeTime === undefined || firstTime === undefined || secondTime === undefined || firstTime <= beforeTime || secondTime < firstTime) {
      lines.push("", "⚠ NOT CONFIRMED: both declaration reads matched, but modification timestamps were missing, invalid, unchanged or regressed. The write was accepted; inspect the connector before relying on it.");
    } else {
      lines.push("", "Applied — connectionParameters OAuth scope declarations confirmed at both observation times.");
      lines.push(`Read back twice (${second.scopes.length}): ${second.scopes.join(" ")}`);
    }
  }
  lines.push(
    "",
    "Stored OpenAPI/root/operation security was not independently read back. Scope declaration reads do not prove effective grants, renewed consent or runtime access. No automatic retry or reauthorization was performed.",
    "This request changes only connector declarations. Existing connections retain their previously consented scopes; changing declarations alone grants nothing.",
  );
  return lines.join("\n");
}

export const listCustomConnectorsTool = {
  name: "list_custom_connectors",
  description: "List all custom connectors in the environment. Shows connector names, IDs, and operation counts.",
  inputSchema: {
    type: "object" as const,
    properties: {
      environment: {
        type: "string",
        description: "Environment ID (uses default if omitted)",
      },
    },
  },
};

export async function handleListCustomConnectors(
  api: ConnectorMetadataApi,
  input: unknown
): Promise<string> {
  const parsed = listCustomConnectorsInputSchema.parse(input === undefined ? {} : input);
  if (parsed.environment !== undefined) api = api.forEnvironment(parsed.environment);
  logger.info("Listing custom connectors");

  const connectors = await api.listCustomConnectors();

  if (connectors.length === 0) {
    return "No custom connectors found in this environment.";
  }

  const lines = [`Custom Connectors (${connectors.length}):`];
  for (const conn of connectors) {
    // The list endpoint does not return the swagger (verified live, including
    // with $expand=swagger, which it ignores), so an absent definition means
    // "not returned here" — NOT "no operations". Reporting 0 read as a broken
    // or empty connector: a live MCP connector with two real operations was
    // listed as "0 operations".
    const paths = conn.properties.swagger?.paths;
    const detail =
      paths !== undefined
        ? `${Object.values(paths).reduce((sum, methods) => sum + Object.keys(methods).length, 0)} operations`
        : "operations not listed here — use get_custom_connector";
    lines.push(`  • ${conn.properties.displayName} (${conn.name}) - ${detail}`);
  }

  return lines.join("\n");
}

// =============================================================================
// Get Custom Connector
// =============================================================================

export const getCustomConnectorInputSchema = z.object({
  connectorId: z.string().describe("The custom connector ID"),
  environment: z.string().optional().describe("Environment ID (uses default if omitted)"),
  includeDefinition: z
    .boolean()
    .optional()
    .describe("Also return the connector's full stored definition as JSON (what the portal actually saved)"),
});

export const getCustomConnectorTool = {
  name: "get_custom_connector",
  description: "Get detailed information about a custom connector including its OpenAPI definition and all operations.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connectorId: {
        type: "string",
        description: "The custom connector ID",
      },
      environment: {
        type: "string",
        description: "Environment ID (uses default if omitted)",
      },
      includeDefinition: {
        type: "boolean",
        description:
          "Also return the full stored definition as JSON — connection parameters, policy templates, OpenAPI document with vendor extensions. Use it to see exactly what the portal saved, e.g. after editing a connector by hand.",
      },
    },
    required: ["connectorId"],
  },
};

/** Largest raw definition printed inline; the OpenAPI document of a big connector can run to megabytes. */
const MAX_RAW_DEFINITION_CHARS = 60_000;

export async function handleGetCustomConnector(
  api: ConnectorMetadataApi,
  input: unknown
): Promise<string> {
  const parsed = getCustomConnectorInputSchema.parse(input);
  if (parsed.environment !== undefined) api = api.forEnvironment(parsed.environment);

  logger.info({ connectorId: parsed.connectorId }, "Getting custom connector");

  const connector = await api.getCustomConnector(parsed.connectorId);

  const lines = [
    `## ${connector.properties.displayName}`,
    ``,
    `**ID:** ${connector.name}`,
    `**Description:** ${connector.properties.description || "N/A"}`,
    `**Base URL:** ${connector.properties.swagger?.host || "N/A"}`,
    `**Created:** ${connector.properties.createdTime || "Unknown"}`,
    ``,
  ];

  // Show authentication
  const connParams = connector.properties.connectionParameters;
  if (connParams && Object.keys(connParams).length > 0) {
    lines.push(`### Authentication`);
    for (const [key, param] of Object.entries(connParams)) {
      const p = param as Record<string, unknown>;
      lines.push(`- **${key}:** ${displayValue(p.type ?? "unknown")}`);
      // An OAuth parameter is only useful if you can see where it sends people
      // and which redirect URL the identity provider must allow. The platform
      // never returns the client secret, so nothing sensitive can print here.
      const oauth = p.oAuthSettings as Record<string, unknown> | undefined;
      if (p.type === "oauthSetting" && oauth) {
        const custom = (oauth.customParameters ?? {}) as Record<string, { value?: unknown } | undefined>;
        const detail = (label: string, value: unknown): void => {
          if (value !== undefined && value !== null && value !== "") lines.push(`  - ${label}: ${displayValue(value)}`);
        };
        detail("identity provider", oauth.identityProvider);
        detail("client id", oauth.clientId);
        detail("redirect URL", oauth.redirectUrl);
        detail("authorization URL", custom.authorizationUrl?.value);
        detail("token URL", custom.tokenUrl?.value);
        detail("refresh URL", custom.refreshUrl?.value);
        if (Array.isArray(oauth.scopes) && oauth.scopes.length > 0) {
          lines.push(`  - scopes: ${(oauth.scopes as unknown[]).map(displayValue).join(" ")}`);
        }
      }
    }
    lines.push(``);
  }

  // Show all operations
  const swagger = connector.properties.swagger;
  if (swagger?.paths) {
    lines.push(`### Operations`);
    for (const [path, methods] of Object.entries(swagger.paths)) {
      for (const [method, op] of Object.entries(methods)) {
        if (typeof op === "object" && op.operationId) {
          const trigger = op["x-ms-trigger"] ? " [Trigger]" : "";
          lines.push(`- **${method.toUpperCase()} ${path}** → ${op.operationId}${trigger}`);
          if (op.summary) lines.push(`  ${op.summary}`);
          if (op.parameters?.length) {
            lines.push(`  Parameters: ${op.parameters.map((p) => p.name ?? p.$ref ?? "(unnamed)").join(", ")}`);
          }
        }
      }
    }
  }

  if (parsed.includeDefinition) {
    // Secrets never come back from the platform (client secrets are write-only),
    // so the stored definition is safe to print verbatim.
    const raw = JSON.stringify(connector.properties, null, 2);
    lines.push(`### Definition (as stored)`);
    lines.push("```json");
    lines.push(raw.length > MAX_RAW_DEFINITION_CHARS ? raw.slice(0, MAX_RAW_DEFINITION_CHARS) + `\n… truncated (${raw.length} chars total)` : raw);
    lines.push("```");
  }

  return lines.join("\n");
}

// =============================================================================
// Create Custom Connector
// =============================================================================

const operationSchema = z.object({
  operationId: z.string().describe("Unique operation ID (e.g., GetUsers, CreateOrder)"),
  method: z.enum(["get", "post", "put", "patch", "delete"]).describe("HTTP method"),
  path: z.string().describe("API path (e.g., /users, /orders/{id})"),
  summary: z.string().optional().describe("Short description of what this operation does"),
  parameters: z.array(z.object({
    name: z.string(),
    in: z.enum(["query", "header", "path", "body"]),
    type: z.string().optional(),
    required: z.boolean().optional(),
    description: z.string().optional(),
  })).optional().describe("Operation parameters"),
  isTrigger: z.boolean().optional().describe("Whether this operation is a trigger (for polling)"),
});

/**
 * Render an API failure with the service's own explanation.
 *
 * The API layer throws `ApiError("<context>: API error", status, details)` with
 * the response body in `details`; reporting only `.message` threw that body
 * away, so a rejected connector definition read as a bare "API error" with no
 * way to tell WHAT the platform objected to.
 */
export function describeApiError(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error);
  if (error && typeof error === "object" && "details" in error) {
    const details = (error as { details?: unknown }).details;
    if (details !== undefined && details !== null && details !== "") {
      const text = typeof details === "string" ? details : JSON.stringify(details);
      return `${msg} — ${text.slice(0, 800)}`;
    }
  }
  return msg;
}

// =============================================================================
// OAuth 2.0 (shared by create_custom_connector and import_openapi_connector)
// =============================================================================

/**
 * Where Power Platform receives the authorization code for a generic OAuth 2.0
 * connector created with `redirectMode: "Global"`. The client registered with
 * the identity provider must list this exact URL.
 */
export const OAUTH2_GLOBAL_REDIRECT_URL = "https://global.consent.azure-apim.net/redirect";

/** Zod fields both connector-creating tools accept when `authType` is `oauth2`. */
const oauth2Fields = {
  oauthClientId: z.string().optional().describe("OAuth 2.0 client id issued by the identity provider (authType oauth2)"),
  oauthClientSecret: z
    .string()
    .optional()
    .describe("OAuth 2.0 client secret. Sent to Power Platform once; never echoed back or logged."),
  oauthAuthorizationUrl: z.string().optional().describe("Authorization endpoint (https)"),
  oauthTokenUrl: z.string().optional().describe("Token endpoint (https)"),
  oauthRefreshUrl: z.string().optional().describe("Refresh endpoint (https). Defaults to the token endpoint."),
  oauthScopes: z.array(z.string()).optional().describe("Scopes to request, e.g. ['read:me', 'offline_access']"),
};

/** JSON-schema mirror of `oauth2Fields` for the tool descriptors. */
const OAUTH2_INPUT_PROPERTIES = {
  oauthClientId: { type: "string", description: "OAuth 2.0 client id (authType oauth2)" },
  oauthClientSecret: {
    type: "string",
    description: "OAuth 2.0 client secret (authType oauth2). Sent once to Power Platform; never echoed or logged.",
  },
  oauthAuthorizationUrl: { type: "string", description: "OAuth 2.0 authorization endpoint, https (authType oauth2)" },
  oauthTokenUrl: { type: "string", description: "OAuth 2.0 token endpoint, https (authType oauth2)" },
  oauthRefreshUrl: {
    type: "string",
    description: "OAuth 2.0 refresh endpoint, https. Defaults to the token endpoint (authType oauth2)",
  },
  oauthScopes: {
    type: "array",
    items: { type: "string" },
    description: "OAuth 2.0 scopes to request (authType oauth2)",
  },
} as const;

type OAuth2Input = {
  oauthClientId?: string | undefined;
  oauthClientSecret?: string | undefined;
  oauthAuthorizationUrl?: string | undefined;
  oauthTokenUrl?: string | undefined;
  oauthRefreshUrl?: string | undefined;
  oauthScopes?: string[] | undefined;
};

/**
 * Build the `token` connection parameter for a generic OAuth 2.0 connector, or
 * return an error string naming what is missing.
 *
 * Before this existed both tools ACCEPTED `authType: "oauth2"` and then built
 * no connection parameters at all, so the connector came out unauthenticated
 * and the caller only found out when the first connection prompted for
 * nothing. Choosing OAuth without the client details is now refused up front.
 */
export function buildOAuth2ConnectionParameter(input: OAuth2Input): ConnectionParameter | string {
  const required: Array<[string, string | undefined]> = [
    ["oauthClientId", input.oauthClientId],
    ["oauthClientSecret", input.oauthClientSecret],
    ["oauthAuthorizationUrl", input.oauthAuthorizationUrl],
    ["oauthTokenUrl", input.oauthTokenUrl],
  ];
  const missing = required.filter(([, v]) => !v || v.trim() === "").map(([k]) => k);
  if (missing.length > 0) {
    return (
      `Error: authType "oauth2" needs ${missing.join(", ")}. ` +
      "Register an OAuth client with the service first and pass its client id, client secret, authorization URL and token URL. " +
      `The client must allow the redirect URL Power Platform assigns to THIS connector — ${OAUTH2_GLOBAL_REDIRECT_URL}/<connector name without the shared_ prefix> — which is reported after creation; ` +
      "for an MCP server that only offers dynamic client registration, POST to its registration_endpoint with that redirect URL to obtain a client id and secret."
    );
  }
  const authorizationUrl = input.oauthAuthorizationUrl!.trim();
  const tokenUrl = input.oauthTokenUrl!.trim();
  const refreshUrl = (input.oauthRefreshUrl ?? tokenUrl).trim();
  validateHttpsUrl(authorizationUrl, "OAuth authorization URL");
  validateHttpsUrl(tokenUrl, "OAuth token URL");
  validateHttpsUrl(refreshUrl, "OAuth refresh URL");
  return {
    type: "oauthSetting",
    oAuthSettings: {
      identityProvider: "oauth2",
      clientId: input.oauthClientId!.trim(),
      clientSecret: input.oauthClientSecret!,
      scopes: input.oauthScopes ?? [],
      redirectMode: "Global",
      redirectUrl: OAUTH2_GLOBAL_REDIRECT_URL,
      properties: { IsFirstParty: "False" },
      customParameters: {
        authorizationUrl: { value: authorizationUrl },
        tokenUrl: { value: tokenUrl },
        refreshUrl: { value: refreshUrl },
      },
    },
  };
}

/** Swagger 2.0 `securityDefinitions` + `security` for a generic OAuth 2.0 connector. */
export function buildOAuth2Security(input: OAuth2Input): {
  securityDefinitions: Record<string, unknown>;
  security: Array<Record<string, string[]>>;
} {
  const scopes = input.oauthScopes ?? [];
  return {
    securityDefinitions: {
      oauth2_auth: {
        type: "oauth2",
        flow: "accessCode",
        authorizationUrl: input.oauthAuthorizationUrl!.trim(),
        tokenUrl: input.oauthTokenUrl!.trim(),
        scopes: Object.fromEntries(scopes.map((s) => [s, s])),
      },
    },
    security: [{ oauth2_auth: scopes }],
  };
}

/**
 * What a caller has to do next after creating an OAuth 2.0 connector.
 *
 * The platform does NOT keep the bare global redirect URL it was sent: the
 * created connector comes back with a per-connector URL,
 * `${OAUTH2_GLOBAL_REDIRECT_URL}/<connector name without shared_>` (seen live).
 * A client registered with only the bare URL fails the sign-in with a
 * redirect mismatch, so the ACTUAL URL is read off the created connector and
 * reported here.
 */
function oauth2NextSteps(connector: { properties: { connectionParameters?: unknown } }): string {
  const params = connector.properties.connectionParameters as
    | Record<string, { oAuthSettings?: { redirectUrl?: unknown } } | undefined>
    | undefined;
  const redirectRaw = params?.["token"]?.oAuthSettings?.redirectUrl;
  const redirectUrl = typeof redirectRaw === "string" && redirectRaw !== "" ? redirectRaw : undefined;
  const redirectLine = redirectUrl
    ? `Power Platform assigned this connector the redirect URL ${redirectUrl} — the OAuth client registered with the identity provider must list EXACTLY that URL (the bare ${OAUTH2_GLOBAL_REDIRECT_URL} is not enough).`
    : "Check the connector's redirect URL with get_custom_connector and make sure the OAuth client registered with the identity provider lists exactly that URL.";
  return (
    "\nOAuth 2.0: " + redirectLine + " " +
    "A connection for this connector is created by a user signing in (Power Apps > Connections, or when the connector is first used), not by this server."
  );
}

/**
 * Parse an OpenAPI/Swagger document given as JSON or YAML.
 *
 * The tool has always advertised YAML — and Microsoft's own MCP-connector
 * examples are YAML — but the implementation only ever called JSON.parse, so
 * every YAML spec was refused as "Invalid JSON".
 */
export function parseOpenApiSpec(text: string): OpenApiSpec | null {
  try {
    const doc: unknown = JSON.parse(text);
    return doc !== null && typeof doc === "object" && !Array.isArray(doc) ? (doc as OpenApiSpec) : null;
  } catch {
    // Not JSON — fall through to YAML.
  }
  try {
    const doc: unknown = parseYaml(text);
    return doc !== null && typeof doc === "object" && !Array.isArray(doc) ? (doc as OpenApiSpec) : null;
  } catch {
    return null;
  }
}

export const createCustomConnectorInputSchema = z.object({
  displayName: z.string().describe("Display name for the connector"),
  description: z.string().optional().describe("Description of what the connector does"),
  apiBaseUrl: z.string().describe("Base URL of the API (e.g., https://api.example.com/v1)"),
  authType: z.enum(["none", "apiKey", "basic", "oauth2"]).describe("Authentication type"),
  ...oauth2Fields,
  apiKeyName: z.string().optional().describe("Header name for API key (default: X-API-Key)"),
  apiKeyLocation: z.enum(["header", "query"]).optional().describe("Where to send API key (default: header)"),
  operations: z.array(operationSchema).describe("List of API operations to include"),
  iconBrandColor: z.string().optional().describe("Brand color in hex (e.g., #007ee5)"),
  environment: z.string().optional().describe("Environment ID (uses default if omitted)"),
});

export const createCustomConnectorTool = {
  name: "create_custom_connector",
  description:
    "Create a custom connector for any REST API. Define the base URL, authentication, and operations. Each operation specifies an HTTP method, path, and parameters.",
  inputSchema: {
    type: "object" as const,
    properties: {
      displayName: {
        type: "string",
        description: "Display name for the connector",
      },
      description: {
        type: "string",
        description: "Description of what the connector does",
      },
      apiBaseUrl: {
        type: "string",
        description: "Base URL of the API (e.g., https://api.example.com/v1)",
      },
      authType: {
        type: "string",
        enum: ["none", "apiKey", "basic", "oauth2"],
        description: "Authentication type",
      },
      ...OAUTH2_INPUT_PROPERTIES,
      apiKeyName: {
        type: "string",
        description: "Header name for API key (default: X-API-Key)",
      },
      apiKeyLocation: {
        type: "string",
        enum: ["header", "query"],
        description: "Where to send API key (default: header)",
      },
      operations: {
        type: "array",
        description: "List of API operations",
        items: {
          type: "object",
          properties: {
            operationId: { type: "string", description: "Unique operation ID" },
            method: { type: "string", enum: ["get", "post", "put", "patch", "delete"] },
            path: { type: "string", description: "API path (e.g., /users/{id})" },
            summary: { type: "string", description: "Operation description" },
            parameters: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: { type: "string" },
                  in: { type: "string", enum: ["query", "header", "path", "body"] },
                  type: { type: "string" },
                  required: { type: "boolean" },
                  description: { type: "string" },
                },
              },
            },
            isTrigger: { type: "boolean", description: "Is this a trigger operation" },
          },
          required: ["operationId", "method", "path"],
        },
      },
      iconBrandColor: {
        type: "string",
        description: "Brand color in hex",
      },
      environment: {
        type: "string",
        description: "Environment ID (uses default if omitted)",
      },
    },
    required: ["displayName", "apiBaseUrl", "authType", "operations"],
  },
};

export async function handleCreateCustomConnector(
  api: ConnectorMetadataApi,
  input: unknown
): Promise<string> {
  const parsed = createCustomConnectorInputSchema.parse(input);
  if (parsed.environment !== undefined) api = api.forEnvironment(parsed.environment);

  logger.info({ displayName: parsed.displayName }, "Creating custom connector");

  if (parsed.operations.length === 0) {
    return "Error: At least one operation is required. Specify the API endpoints you want to use.";
  }

  // Build connection parameters based on auth type
  const connectionParameters: Record<string, unknown> = {};

  if (parsed.authType === "apiKey") {
    connectionParameters["api_key"] = {
      type: "securestring",
      uiDefinition: {
        displayName: "API Key",
        description: `Enter your API key for ${parsed.displayName}`,
        tooltip: "API Key for authentication",
        constraints: { required: "true" },
      },
    };
  } else if (parsed.authType === "basic") {
    connectionParameters["username"] = {
      type: "string",
      uiDefinition: {
        displayName: "Username",
        description: "Enter your username",
        constraints: { required: "true" },
      },
    };
    connectionParameters["password"] = {
      type: "securestring",
      uiDefinition: {
        displayName: "Password",
        description: "Enter your password",
        constraints: { required: "true" },
      },
    };
  } else if (parsed.authType === "oauth2") {
    const token = buildOAuth2ConnectionParameter(parsed);
    if (typeof token === "string") return token;
    connectionParameters["token"] = token;
  }

  // Enforce HTTPS for custom connector base URL
  validateHttpsUrl(parsed.apiBaseUrl, "Custom connector base URL");

  // SSRF protection: block private/internal addresses
  const url = new URL(parsed.apiBaseUrl);
  if (isPrivateHost(url.hostname)) {
    return "Error: Base URL points to a private/internal address. Custom connectors must use public endpoints.";
  }

  // Build OpenAPI spec from operations
  const paths: Record<string, Record<string, unknown>> = {};

  for (const op of parsed.operations) {
    if (!paths[op.path]) {
      paths[op.path] = {};
    }

    const operation: Record<string, unknown> = {
      operationId: op.operationId,
      summary: op.summary || op.operationId,
      responses: {
        "200": { description: "Success" },
        "400": { description: "Bad Request" },
        "401": { description: "Unauthorized" },
      },
    };

    if (op.parameters?.length) {
      operation.parameters = op.parameters.map(p => ({
        name: p.name,
        in: p.in,
        type: p.type || "string",
        required: p.required ?? (p.in === "path"),
        description: p.description,
        "x-ms-summary": p.name,
      }));
    }

    if (op.isTrigger) {
      operation["x-ms-trigger"] = "batch";
    }

    paths[op.path]![op.method] = operation;
  }

  const swagger: OpenApiSpec = {
    swagger: "2.0",
    info: {
      title: parsed.displayName,
      description: parsed.description || `Custom connector for ${parsed.displayName}`,
      version: "1.0.0",
    },
    host: url.host,
    basePath: url.pathname || "/",
    schemes: [url.protocol.replace(":", "")],
    consumes: ["application/json"],
    produces: ["application/json"],
    paths: paths as OpenApiSpec["paths"],
    securityDefinitions: parsed.authType === "apiKey" ? {
      api_key: {
        type: "apiKey",
        name: parsed.apiKeyName || "X-API-Key",
        in: parsed.apiKeyLocation || "header",
      },
    } : parsed.authType === "oauth2" ? buildOAuth2Security(parsed).securityDefinitions : undefined,
    ...(parsed.authType === "oauth2" ? { security: buildOAuth2Security(parsed).security } : {}),
  };

  const definition: CustomConnectorDefinition = {
    properties: {
      displayName: parsed.displayName,
      description: parsed.description,
      iconBrandColor: parsed.iconBrandColor || "#007ee5",
      connectionParameters: Object.keys(connectionParameters).length > 0
        ? connectionParameters as Record<string, ConnectionParameter>
        : undefined,
      swagger,
      backendService: {
        serviceUrl: parsed.apiBaseUrl,
      },
    },
  };

  try {
    const connector = await api.createCustomConnector(definition);
    const opList = parsed.operations.map(o => `${o.method.toUpperCase()} ${o.path}`).join(", ");
    return `Created "${connector.properties.displayName}" (${connector.name}) with ${parsed.operations.length} operations: ${opList}${parsed.authType === "oauth2" ? oauth2NextSteps(connector) : ""}`;
  } catch (error) {
    return `Error creating connector: ${describeApiError(error)}`;
  }
}

// =============================================================================
// Update Custom Connector (Add Operations)
// =============================================================================

/** Generic credential replacement is not a provider/public-client migration. */
function planGenericOAuthReplacement(
  rawParameters: unknown,
  swagger: OpenApiSpec,
  input: OAuth2Input,
): { connectionParameters: Record<string, ConnectionParameter>; swagger: OpenApiSpec } | string {
  const refused = "Refusing to replace OAuth: only one existing generic oauth2 confidential-client configuration with readable metadata is supported. Dynamic, public-client, unknown or incompatible authentication must use its provider-specific setup. No connector update was sent.";
  const parameters = diagnosticRecord(rawParameters);
  if (!parameters || Object.keys(parameters).length > 100) return refused;
  const entries = Object.entries(parameters);
  if (entries.some(([, value]) => !diagnosticRecord(value))) return refused;
  const oauthEntries = entries.filter(([, value]) => diagnosticRecord(value)?.type === "oauthSetting");
  if (oauthEntries.length !== 1) return refused;
  const [key, rawParameter] = oauthEntries[0]!;
  const parameter = diagnosticRecord(rawParameter)!;
  const oauth = diagnosticRecord(parameter.oAuthSettings);
  const knownOAuthKeys = new Set(["identityProvider", "clientId", "clientSecret", "redirectMode", "redirectUrl", "scopes", "properties", "customParameters"]);
  if (!oauth || oauth.identityProvider !== "oauth2" ||
      typeof oauth.clientId !== "string" || !oauth.clientId.trim() || oauth.clientId.length > 2048 ||
      Object.keys(oauth).some((name) => !knownOAuthKeys.has(name)) ||
      (oauth.redirectMode !== undefined && oauth.redirectMode !== "Global")) return refused;
  // Absence of the write-only secret is normal, not proof of a public client.
  // Unknown auth-method/PKCE/DCR properties cannot safely be reset by this builder.
  if (oauth.properties !== undefined) {
    const properties = diagnosticRecord(oauth.properties);
    if (!properties || Object.keys(properties).some((name) => name !== "IsFirstParty") ||
        (properties.IsFirstParty !== undefined && properties.IsFirstParty !== "False" && properties.IsFirstParty !== false)) return refused;
  }
  if (oauth.customParameters !== undefined) {
    const custom = diagnosticRecord(oauth.customParameters);
    if (!custom || Object.keys(custom).some((name) => !["authorizationUrl", "tokenUrl", "refreshUrl"].includes(name)) ||
        Object.values(custom).some((value) => {
          const endpoint = diagnosticRecord(value);
          return !endpoint || typeof endpoint.value !== "string" || Object.keys(endpoint).some((name) => name !== "value");
        })) return refused;
  }
  if (!Array.isArray(oauth.scopes) || oauth.scopes.length > 512 ||
      !oauth.scopes.every((scope): scope is string => typeof scope === "string" && scope.length <= 256 && OAUTH_SCOPE_TOKEN.test(scope))) return refused;
  const scopes = input.oauthScopes ?? oauth.scopes;
  if (!Array.isArray(scopes) || scopes.length > 512 ||
      !scopes.every((scope): scope is string => typeof scope === "string" && scope.length <= 256 && OAUTH_SCOPE_TOKEN.test(scope))) return refused;
  const replacement = buildOAuth2ConnectionParameter({ ...input, oauthScopes: scopes });
  if (typeof replacement === "string") return replacement;
  const plan = planScopeSwaggerUpdate(swagger, scopes);
  if (typeof plan === "string") return plan;
  const definitions = plan.swagger.securityDefinitions!;
  const name = Object.keys(definitions).find((candidate) => diagnosticRecord(definitions[candidate])?.type === "oauth2")!;
  const scheme = diagnosticRecord(definitions[name])!;
  if (scheme.flow !== "accessCode") return refused;
  // Credential-only replacement must not expand an existing root scope subset
  // or rewrite its declaration descriptions. Explicit scope changes use the plan.
  if (input.oauthScopes === undefined) {
    scheme.scopes = diagnosticRecord(swagger.securityDefinitions?.[name])?.scopes;
    if (swagger.security === undefined) delete plan.swagger.security;
    else plan.swagger.security = swagger.security;
  }
  definitions[name] = {
    ...scheme,
    authorizationUrl: input.oauthAuthorizationUrl!.trim(),
    tokenUrl: input.oauthTokenUrl!.trim(),
  };
  return {
    connectionParameters: {
      ...parameters as Record<string, ConnectionParameter>,
      [key]: {
        ...parameter,
        oAuthSettings: {
          ...oauth,
          ...replacement.oAuthSettings,
          ...(oauth.redirectUrl !== undefined ? { redirectUrl: oauth.redirectUrl } : {}),
          ...(oauth.properties !== undefined ? { properties: oauth.properties } : {}),
        },
      } as ConnectionParameter,
    },
    swagger: plan.swagger,
  };
}

export const updateCustomConnectorInputSchema = z.object({
  connectorId: z.string().describe("The custom connector ID to update"),
  addOperations: z.array(operationSchema).optional().describe("New operations to add"),
  removeOperations: z.array(z.string()).optional().describe("Operation IDs to remove"),
  displayName: z
    .string()
    .optional()
    .describe("Not updatable — the platform refuses display-name changes; delete and recreate to rename"),
  description: z.string().optional().describe("New description"),
  environment: z.string().optional().describe("Environment ID (uses default if omitted)"),
  ...oauth2Fields,
});

export const updateCustomConnectorTool = {
  name: "update_custom_connector",
  description: "Update an existing custom connector's operations or description using its original uploaded Swagger, never its rewritten proxy definition. OAuth replacement requires an existing compatible generic confidential-client configuration and preserves unrelated authentication and explicit operation security; dynamic/public-client provider migrations are refused. Display names cannot be changed. Saved metadata does not prove renewed consent or runtime access.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connectorId: {
        type: "string",
        description: "The custom connector ID to update",
      },
      addOperations: {
        type: "array",
        description: "New operations to add",
        items: {
          type: "object",
          properties: {
            operationId: { type: "string" },
            method: { type: "string", enum: ["get", "post", "put", "patch", "delete"] },
            path: { type: "string" },
            summary: { type: "string" },
            parameters: { type: "array" },
            isTrigger: { type: "boolean" },
          },
          required: ["operationId", "method", "path"],
        },
      },
      removeOperations: {
        type: "array",
        items: { type: "string" },
        description: "Operation IDs to remove",
      },
      displayName: {
        type: "string",
        description:
          "Not updatable: the platform refuses CannotUpdateApiDisplayName. Delete and recreate the connector to rename it.",
      },
      description: {
        type: "string",
        description: "New description",
      },
      environment: {
        type: "string",
        description: "Environment ID",
      },
      ...OAUTH2_INPUT_PROPERTIES,
    },
    required: ["connectorId"],
  },
};

export async function handleUpdateCustomConnector(
  api: ConnectorMetadataApi,
  input: unknown
): Promise<string> {
  const parsed = updateCustomConnectorInputSchema.parse(input);
  if (parsed.environment !== undefined) api = api.forEnvironment(parsed.environment);

  logger.info({ connectorId: parsed.connectorId }, "Updating custom connector");

  // Get existing connector
  const existing = await api.getCustomConnector(parsed.connectorId);

  // The platform refuses a display-name change outright (CannotUpdateApiDisplayName),
  // and it refuses the WHOLE update when the property is merely present — which is
  // why this tool failed on every call before it stopped sending the name.
  if (parsed.displayName !== undefined && parsed.displayName !== existing.properties.displayName) {
    return `Error: a custom connector's display name cannot be changed after creation (the platform answers CannotUpdateApiDisplayName). Delete "${existing.properties.displayName}" and create it again under the new name — note the connector id and its OAuth redirect URL change with it.`;
  }

  // Edit the document as it was UPLOADED. The stored copy is the platform's
  // rewrite (proxy host, /{connectionId} prefixes, generated GET twins) and
  // writing it back would point the connector at its own proxy.
  let original: OpenApiSpec | null = null;
  try {
    if (typeof api.getCustomConnectorOriginalDefinition === "function") {
      original = await api.getCustomConnectorOriginalDefinition(existing);
    }
  } catch { /* Never echo a signed original-definition URL or upstream body. */ }
  if (!original) return "Refusing to write: the original uploaded Swagger definition is unavailable. The stored proxy-rewritten Swagger is not a safe fallback. No connector update was sent.";
  let swagger: OpenApiSpec;
  try {
    const document = diagnosticRecord(original);
    if (document?.swagger !== "2.0" || !diagnosticRecord(document.paths)) throw new Error();
    // Do not modify the API's returned object while planning a later refusal.
    swagger = structuredClone(original);
  } catch {
    return "Refusing to write: the original uploaded Swagger definition is malformed or unsupported. No connector update was sent.";
  }

  const oauthRequested =
    parsed.oauthClientId !== undefined || parsed.oauthClientSecret !== undefined ||
    parsed.oauthAuthorizationUrl !== undefined || parsed.oauthTokenUrl !== undefined ||
    parsed.oauthRefreshUrl !== undefined || parsed.oauthScopes !== undefined;
  let connectionParameters = existing.properties.connectionParameters as Record<string, ConnectionParameter> | undefined;
  if (oauthRequested) {
    const plan = planGenericOAuthReplacement(connectionParameters, swagger, parsed);
    if (typeof plan === "string") return plan;
    connectionParameters = plan.connectionParameters;
    swagger = plan.swagger;
  }

  // Remove operations
  if (parsed.removeOperations?.length) {
    for (const opId of parsed.removeOperations) {
      for (const [path, methods] of Object.entries(swagger.paths ?? {})) {
        if (!methods) continue;
        for (const [method, op] of Object.entries(methods)) {
          if (typeof op === "object" && op?.operationId === opId) {
            delete (swagger.paths as Record<string, Record<string, unknown>>)[path]![method];
            if (Object.keys((swagger.paths as Record<string, Record<string, unknown>>)[path]!).length === 0) {
              delete (swagger.paths as Record<string, unknown>)[path];
            }
          }
        }
      }
    }
  }

  // Add new operations
  if (parsed.addOperations?.length) {
    for (const op of parsed.addOperations) {
      if (!swagger.paths[op.path]) {
        swagger.paths[op.path] = {};
      }

      if (Object.hasOwn(swagger.paths[op.path]!, op.method)) {
        return "Refusing to overwrite an existing operation through addOperations: its security and other settings would be lost. No connector update was sent.";
      }

      const operation: Record<string, unknown> = {
        operationId: op.operationId,
        summary: op.summary || op.operationId,
        responses: {
          "200": { description: "Success" },
        },
      };

      if (op.parameters?.length) {
        operation.parameters = op.parameters.map(p => ({
          name: p.name,
          in: p.in,
          type: p.type || "string",
          required: p.required ?? (p.in === "path"),
          description: p.description,
        }));
      }

      if (op.isTrigger) {
        operation["x-ms-trigger"] = "batch";
      }

      if (swagger.paths[op.path]) {
        (swagger.paths[op.path] as Record<string, unknown>)[op.method] = operation;
      }
    }
  }

  // The backend is a stored property, not something to re-derive from the
  // stored swagger (whose host is the APIM proxy).
  const backendService = existing.properties.backendService?.serviceUrl
    ? { serviceUrl: existing.properties.backendService.serviceUrl }
    : swagger.host
      ? { serviceUrl: `https://${swagger.host}${swagger.basePath || ""}` }
      : undefined;

  const definition: CustomConnectorDefinition = {
    properties: {
      description: parsed.description ?? existing.properties.description,
      iconBrandColor: existing.properties.iconBrandColor,
      connectionParameters,
      swagger,
      backendService,
      ...(existing.properties.policyTemplateInstances !== undefined
        ? { policyTemplateInstances: existing.properties.policyTemplateInstances }
        : {}),
      ...(existing.properties.capabilities !== undefined ? { capabilities: existing.properties.capabilities } : {}),
    },
  };

  try {
    const updated = await api.updateCustomConnector(parsed.connectorId, definition);
    const changes: string[] = [];
    if (parsed.addOperations?.length) changes.push(`added ${parsed.addOperations.length} operations`);
    if (parsed.removeOperations?.length) changes.push(`removed ${parsed.removeOperations.length} operations`);
    if (parsed.description !== undefined) changes.push("description updated");
    if (oauthRequested) changes.push(`OAuth client set to ${parsed.oauthClientId ?? ""}`.trimEnd());
    let text = `Updated "${updated.properties.displayName ?? existing.properties.displayName}": ${changes.join(", ") || "no changes"}`;
    if (oauthRequested) text += oauth2NextSteps(updated);
    return text;
  } catch {
    return "Error updating connector: the update outcome could not be verified. Inspect the connector before deciding on another change; do not retry automatically.";
  }
}

// =============================================================================
// Delete Custom Connector
// =============================================================================

export const deleteCustomConnectorInputSchema = z.object({
  connectorId: z.string().describe("The custom connector ID to delete"),
  confirm: z.boolean().optional().describe("Set to true to confirm deletion"),
  environment: z.string().optional().describe("Environment ID (uses default if omitted)"),
});

export const deleteCustomConnectorTool = {
  name: "delete_custom_connector",
  description: "Delete a custom connector. Requires confirm=true to proceed.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connectorId: {
        type: "string",
        description: "The custom connector ID to delete",
      },
      confirm: {
        type: "boolean",
        description: "Set to true to confirm deletion",
      },
      environment: {
        type: "string",
        description: "Environment ID (uses default if omitted)",
      },
    },
    required: ["connectorId"],
  },
};

export async function handleDeleteCustomConnector(
  api: ConnectorMetadataApi,
  input: unknown
): Promise<string> {
  const parsed = deleteCustomConnectorInputSchema.parse(input);
  if (parsed.environment !== undefined) api = api.forEnvironment(parsed.environment);

  if (!parsed.confirm) {
    // The name lookup is best-effort and must stay INSIDE the try; throwing there
    // would have the bare catch swallow it and always fall back to the id.
    let label = `connector ${parsed.connectorId}`;
    try {
      const connector = await api.getCustomConnector(parsed.connectorId);
      label = `"${connector.properties.displayName}"`;
    } catch {
      // unreadable connector: the id alone is enough to confirm against
    }
    throw new ConfirmationRequiredError(`Confirm deletion of ${label}? Set confirm=true to proceed.`);
  }

  logger.info({ connectorId: parsed.connectorId }, "Deleting custom connector");

  await api.deleteCustomConnector(parsed.connectorId);
  return `Deleted custom connector ${parsed.connectorId}`;
}

// =============================================================================
// Import OpenAPI Spec
// =============================================================================

export const importOpenApiInputSchema = z.object({
  displayName: z.string().describe("Display name for the connector"),
  openApiSpec: z.string().describe("OpenAPI/Swagger specification as JSON or YAML text"),
  authType: z.enum(["none", "apiKey", "basic", "oauth2"]).describe("Authentication type to use"),
  ...oauth2Fields,
  apiKeyName: z.string().optional().describe("Header name for API key"),
  environment: z.string().optional().describe("Environment ID"),
});

export const importOpenApiTool = {
  name: "import_openapi_connector",
  description: "Create a custom connector by importing an OpenAPI/Swagger specification. Provide the full spec and authentication details.",
  inputSchema: {
    type: "object" as const,
    properties: {
      displayName: {
        type: "string",
        description: "Display name for the connector",
      },
      openApiSpec: {
        type: "string",
        description: "OpenAPI/Swagger specification as JSON or YAML text",
      },
      authType: {
        type: "string",
        enum: ["none", "apiKey", "basic", "oauth2"],
        description: "Authentication type",
      },
      ...OAUTH2_INPUT_PROPERTIES,
      apiKeyName: {
        type: "string",
        description: "Header name for API key",
      },
      environment: {
        type: "string",
        description: "Environment ID",
      },
    },
    required: ["displayName", "openApiSpec", "authType"],
  },
};

export async function handleImportOpenApi(
  api: ConnectorMetadataApi,
  input: unknown
): Promise<string> {
  const parsed = importOpenApiInputSchema.parse(input);
  if (parsed.environment !== undefined) api = api.forEnvironment(parsed.environment);

  logger.info({ displayName: parsed.displayName }, "Importing OpenAPI connector");

  const swagger = parseOpenApiSpec(parsed.openApiSpec);
  if (swagger === null) {
    return "Error: The specification is neither valid JSON nor valid YAML. Provide an OpenAPI/Swagger document.";
  }

  // Validate it has paths
  if (!swagger.paths || Object.keys(swagger.paths).length === 0) {
    return "Error: OpenAPI spec must contain at least one path/operation.";
  }

  // Build connection parameters
  const connectionParameters: Record<string, unknown> = {};
  if (parsed.authType === "apiKey") {
    connectionParameters["api_key"] = {
      type: "securestring",
      uiDefinition: {
        displayName: "API Key",
        description: "Enter your API key",
        constraints: { required: "true" },
      },
    };
  } else if (parsed.authType === "basic") {
    connectionParameters["username"] = {
      type: "string",
      uiDefinition: { displayName: "Username", constraints: { required: "true" } },
    };
    connectionParameters["password"] = {
      type: "securestring",
      uiDefinition: { displayName: "Password", constraints: { required: "true" } },
    };
  } else if (parsed.authType === "oauth2") {
    const token = buildOAuth2ConnectionParameter(parsed);
    if (typeof token === "string") return token;
    connectionParameters["token"] = token;
  }

  // Add security definitions if needed
  if (parsed.authType === "apiKey" && !swagger.securityDefinitions) {
    swagger.securityDefinitions = {
      api_key: {
        type: "apiKey",
        name: parsed.apiKeyName || "X-API-Key",
        in: "header",
      },
    };
  }
  if (parsed.authType === "oauth2" && !swagger.securityDefinitions) {
    const sec = buildOAuth2Security(parsed);
    swagger.securityDefinitions = sec.securityDefinitions;
    swagger.security = sec.security;
  }

  const host = swagger.host || swagger.servers?.[0]?.url?.replace(/^https?:\/\//, "").split("/")[0];
  const basePath = swagger.basePath || "/";

  // SSRF protection: block private/internal addresses in OpenAPI host
  if (host && isPrivateHost(host.split(":")[0]!)) {
    return "Error: OpenAPI host points to a private/internal address. Custom connectors must use public endpoints.";
  }

  const definition: CustomConnectorDefinition = {
    properties: {
      displayName: parsed.displayName,
      description: swagger.info?.description,
      iconBrandColor: "#007ee5",
      connectionParameters: Object.keys(connectionParameters).length > 0 ? connectionParameters as Record<string, ConnectionParameter> : undefined,
      swagger,
      backendService: host ? { serviceUrl: (() => {
        const serviceUrl = `https://${host}${basePath}`;
        validateHttpsUrl(serviceUrl, "OpenAPI connector service URL");
        return serviceUrl;
      })() } : undefined,
    },
  };

  try {
    const connector = await api.createCustomConnector(definition);
    const opCount = Object.values(swagger.paths).reduce(
      (sum, methods) => sum + Object.keys(methods).length,
      0
    );
    return `Imported "${connector.properties.displayName}" (${connector.name}) with ${opCount} operations from OpenAPI spec${parsed.authType === "oauth2" ? oauth2NextSteps(connector) : ""}`;
  } catch (error) {
    return `Error importing connector: ${describeApiError(error)}`;
  }
}

// Re-export for backwards compatibility with existing plan tool name
export const planCustomConnectorInputSchema = z.object({
  description: z.string().describe("Describe what API you want to connect to"),
});

export const planCustomConnectorTool = {
  name: "plan_custom_connector",
  description: "Get guidance on creating a custom connector. Describes what information you need to gather about your API.",
  inputSchema: {
    type: "object" as const,
    properties: {
      description: {
        type: "string",
        description: "Describe the API you want to connect to",
      },
    },
    required: ["description"],
  },
};

// eslint-disable-next-line @typescript-eslint/require-await -- Conforms to the shared tool-handler signature, which is uniformly async.
/**
 * The MCP/OAuth connector procedure, as the portal and this server actually
 * require it. Every step here was learned on a live tenant: the redirect URL
 * that only exists after creation, the immutable display name, the portal
 * re-asking for the client secret, and the tool sequence that replaces the
 * portal for everything except the user's own sign-in.
 */
function mcpOAuthConnectorWalkthrough(): string {
  return `
### MCP server or OAuth 2.0 connector — step by step

**What to gather**
- Server URL (https) and path, e.g. \`https://mcp.example.com/v2/mcp\`. Copilot Studio supports the **streamable HTTP** transport only.
- Authentication: none, API key, or OAuth 2.0. For OAuth 2.0: authorization URL, token URL, refresh URL (usually the token URL), scopes. Check \`/.well-known/oauth-authorization-server\` on the server — a \`registration_endpoint\` means it offers dynamic client registration and you can create the OAuth client yourself.

**With this server (replaces the portal for everything but the sign-in)**
1. \`import_openapi_connector\` with a Swagger 2.0 document (JSON or YAML) whose POST path carries \`x-ms-agentic-protocol: mcp-streamable-1.0\` and \`operationId: InvokeMCP\`:
\`\`\`yaml
swagger: '2.0'
info: { title: My MCP, description: What the server does, version: 1.0.0 }
host: mcp.example.com
basePath: /
schemes: [https]
paths:
  /v2/mcp:
    post:
      summary: My MCP Server
      x-ms-agentic-protocol: mcp-streamable-1.0
      operationId: InvokeMCP
      responses: { '200': { description: Success } }
\`\`\`
   For OAuth pass \`authType: oauth2\` with oauthClientId / oauthClientSecret / oauthAuthorizationUrl / oauthTokenUrl / oauthScopes. If the OAuth client does not exist yet, use a placeholder id and secret — the redirect URL you must register does not exist until the connector does.
2. \`get_custom_connector\` → **Authentication → redirect URL**. It is per connector: \`${OAUTH2_GLOBAL_REDIRECT_URL}/<connector name without shared_>\`. Register (or update) the OAuth client with the identity provider using EXACTLY that URL — for dynamic client registration, POST to the registration_endpoint with \`redirect_uris: [that URL]\`.
3. \`update_custom_connector\` with the real oauthClientId / oauthClientSecret / URLs / scopes. The connector keeps its id, so the redirect URL does not move.
4. **Connection (the user's step):** Power Apps or Power Automate → Connections → New connection → the connector → Create → sign in and consent. This server cannot sign in for anyone. Use \`list_connections\` afterwards to get the connection id.
5. **Copilot Studio:** the agent needs generative orchestration on (\`configure_copilot_agent\`). Add the tool in the designer (Tools → Add a tool → search the connector name → pick the connection → Add to agent → Publish), or \`add_copilot_agent_tool\` with mcpServer=true, connectorId, connectionId and operationId InvokeMCP, then \`publish_copilot_agent\`.

**The same thing in the portal** (make.powerautomate.com → Custom connectors): New custom connector → Import an OpenAPI file → **General** (host, base URL) → **Security** (OAuth 2.0, identity provider Generic Oauth 2, client id, client secret, authorization/token/refresh URL, scopes) → **Definition** (the action with the agentic protocol; use the Swagger editor to keep \`x-ms-agentic-protocol\`) → **Create connector** → copy the **Redirect URL** that now appears on Security → register it with the identity provider → back on Security re-enter the client secret and **Update connector**.

**Gotchas**
- A connector cannot be renamed after creation (the platform refuses CannotUpdateApiDisplayName); its id — and so its redirect URL — derives from the name. Pick the final name first.
- The portal asks for the client secret again every time you Update an OAuth connector.
- Data policies (DLP) that govern custom connectors also govern MCP servers added this way.
- A connector with a placeholder client will fail sign-in with a redirect or client error until step 3 is done; that is expected, not a broken connector.
`;
}

export async function handlePlanCustomConnector(
  _api: ConnectorMetadataApi | null,
  input: unknown
): Promise<string> {
  const parsed = planCustomConnectorInputSchema.parse(input);

  const wantsMcpOrOAuth = /\b(mcp|model context protocol|streamable|oauth)\b/i.test(parsed.description);
  const mcpGuide = wantsMcpOrOAuth ? mcpOAuthConnectorWalkthrough() : "";

  return `## Custom Connector Guide

**Your goal:** ${parsed.description}

### Information to Gather

1. **API Base URL**
   - The root URL where the API is hosted
   - Example: https://api.yourservice.com/v1

2. **Authentication**
   - API Key: Get the key and know which header it goes in
   - Basic Auth: Username and password
   - OAuth2: Client ID, Client Secret, Auth URL, Token URL

3. **Operations** - For each API endpoint you need:
   - HTTP Method (GET, POST, PUT, DELETE)
   - Path (e.g., /users, /orders/{id})
   - Parameters (query strings, path variables, request body)
   - What it returns

### Example: Creating a Connector

Once you have the details, use \`create_custom_connector\`:

\`\`\`json
{
  "displayName": "My API",
  "apiBaseUrl": "https://api.example.com/v1",
  "authType": "apiKey",
  "apiKeyName": "Authorization",
  "operations": [
    {
      "operationId": "GetUsers",
      "method": "get",
      "path": "/users",
      "summary": "List all users"
    },
    {
      "operationId": "GetUser",
      "method": "get",
      "path": "/users/{id}",
      "summary": "Get user by ID",
      "parameters": [
        { "name": "id", "in": "path", "required": true }
      ]
    },
    {
      "operationId": "CreateUser",
      "method": "post",
      "path": "/users",
      "summary": "Create a new user"
    }
  ]
}
\`\`\`

### Alternative: Import OpenAPI Spec

If you have an OpenAPI/Swagger spec, use \`import_openapi_connector\` instead.

### Next Steps

1. Get your API documentation
2. Identify the endpoints you need
3. Note the authentication method
4. Use \`create_custom_connector\` or \`import_openapi_connector\`
5. Test by creating a connection in Power Automate\n${mcpGuide}`;
}
